// Comprehensive logging utility for API calls and errors
class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 100; // Keep last 100 logs
  }

  log(level, message, data = null) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      url: window.location.href,
      userAgent: navigator.userAgent.substring(0, 100)
    };

    this.logs.push(logEntry);
    
    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output with styling
    const style = this.getConsoleStyle(level);
    console.log(`%c[${level.toUpperCase()}] ${message}`, style, data || '');

    // Store in localStorage for persistence
    try {
      localStorage.setItem('mba-simulator-logs', JSON.stringify(this.logs.slice(-20)));
    } catch (e) {
      console.warn('Failed to save logs to localStorage:', e);
    }
  }

  getConsoleStyle(level) {
    const styles = {
      info: 'color: #2563eb; font-weight: normal;',
      warn: 'color: #d97706; font-weight: bold;',
      error: 'color: #dc2626; font-weight: bold; background: #fef2f2; padding: 2px 4px;',
      debug: 'color: #6b7280; font-style: italic;',
      api: 'color: #059669; font-weight: bold;',
      success: 'color: #10b981; font-weight: bold; background: #f0fdf4; padding: 2px 4px;'
    };
    return styles[level] || styles.info;
  }

  info(message, data) {
    this.log('info', message, data);
  }

  warn(message, data) {
    this.log('warn', message, data);
  }

  error(message, data) {
    this.log('error', message, data);
  }

  debug(message, data) {
    this.log('debug', message, data);
  }

  api(message, data) {
    this.log('api', message, data);
  }

  success(message, data) {
    this.log('success', message, data);
  }

  // API-specific logging methods
  apiCall(method, endpoint, requestData) {
    this.api(`API Call: ${method} ${endpoint}`, {
      method,
      endpoint,
      requestData: requestData ? JSON.stringify(requestData).substring(0, 500) : null,
      timestamp: new Date().toISOString()
    });
  }

  apiResponse(endpoint, responseData, duration) {
    this.api(`API Response: ${endpoint}`, {
      endpoint,
      responseSize: JSON.stringify(responseData).length,
      duration: `${duration}ms`,
      success: true,
      timestamp: new Date().toISOString()
    });
  }

  apiError(endpoint, error, duration) {
    this.error(`API Error: ${endpoint}`, {
      endpoint,
      error: error.message,
      stack: error.stack?.substring(0, 500),
      duration: duration ? `${duration}ms` : null,
      timestamp: new Date().toISOString()
    });
  }

  // Get logs for admin panel
  getLogs(level = null) {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return this.logs;
  }

  // Export logs for debugging
  exportLogs() {
    const logsData = {
      exported: new Date().toISOString(),
      version: '1.0',
      logs: this.logs
    };
    
    const blob = new Blob([JSON.stringify(logsData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mba-simulator-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
    localStorage.removeItem('mba-simulator-logs');
    this.info('Logs cleared');
  }
}

// Create singleton instance
export const logger = new Logger();

// Load persisted logs on startup
try {
  const savedLogs = localStorage.getItem('mba-simulator-logs');
  if (savedLogs) {
    const parsedLogs = JSON.parse(savedLogs);
    logger.logs = parsedLogs;
    logger.debug('Loaded persisted logs from localStorage', { count: parsedLogs.length });
  }
} catch (e) {
  console.warn('Failed to load persisted logs:', e);
}

// Expose logger functions globally for admin access
if (typeof window !== 'undefined') {
  window.exportLogs = () => logger.exportLogs();
  window.clearLogs = () => logger.clearLogs();
  window.showLogs = (level = null) => {
    const logs = logger.getLogs(level);
    console.table(logs);
    return logs;
  };
} 